import {
  emptyMessage,
  numberBetweenMessage,
  numberMessage,
  oneOfMessage
} from 'messages'
import {
  PRODUCT_MODEL_COLOR,
  PRODUCT_MODEL_DATA,
  PRODUCT_MODEL_DATA_SEPARATOR,
  PRODUCT_MODEL_REGEX,
  PRODUCT_MODEL_SIZE,
  PRODUCT_MODEL_TYPE
} from 'models/daos/product/constants'
import { splitAndTrimAll } from 'utils/string'
import sortByArrayUtil from 'packages/src/modules/array/sort-by/index.js'

const VALIDATOR = {
  data,
  productQuantities,
  promoCodes,
  type
}

const DATA_KEY_TO_VALIDATOR = {
  [PRODUCT_MODEL_DATA.COLOR]: dataColor,
  [PRODUCT_MODEL_DATA.WEIGHT]: dataWeight
}

function getValidatorProductUtil (name) {
  return VALIDATOR[name]
}

function type (value) {
  const types = Object.values(PRODUCT_MODEL_TYPE)
  if (!types.includes(parseInt(value, 10))) {
    return oneOfMessage('type', types)
  }
  return true
}

function productQuantities (value) {
  // If there is no product quantities, no-op
  const chunks = splitAndTrimAll(value, PRODUCT_MODEL_DATA_SEPARATOR)
  if (!chunks.length) {
    return emptyMessage('productQuantities')
  }
  // Validate every pair of size and quantity
  let validateValue = true
  chunks.every(chunk => {
    const chunkArr = getKeyValueArr(chunk)
    const size = chunkArr[0]
    if (!size || !Object.values(PRODUCT_MODEL_SIZE).includes(size)) {
      validateValue = oneOfMessage('size', Object.values(PRODUCT_MODEL_SIZE))
      return false
    }
    const quantity = chunkArr[1]
    if (!quantity || !Number.isInteger(parseInt(quantity, 10))) {
      validateValue = numberMessage('quantity')
      return false
    }
    return true
  })
  return validateValue
}

function promoCodes (value) {
  if (!value) {
    return true
  }
  // Validate every pair of promo code value to expire in days
  const keyValueArr = getKeyValueArr(value)
  const promoCodeValue = keyValueArr[0]
  if (!promoCodeValue || !Number.isFinite(parseFloat(promoCodeValue))) {
    return numberMessage('promo code’s value')
  }
  if (promoCodeValue > 1) {
    return numberBetweenMessage('promo code’s value', 0, 1)
  }
  const expireAtInDays = keyValueArr[1]
  if (!expireAtInDays || !Number.isInteger(parseInt(expireAtInDays, 10))) {
    return numberMessage('expire at in days')
  }
  return true
}

function data (value) {
  const chunks = splitAndTrimAll(value, PRODUCT_MODEL_DATA_SEPARATOR)
  let validateValue = true
  chunks.every(chunk => {
    const chunkArr = getKeyValueArr(chunk)
    const validator = DATA_KEY_TO_VALIDATOR[chunkArr[0]]
    if (!validator) {
      return true
    }
    const validateValueAttr = validator(chunkArr[1])
    validateValue = validateValueAttr
    return validateValueAttr === true
  })
  return validateValue
}

function dataColor (value) {
  if (!value || !Object.values(PRODUCT_MODEL_COLOR).includes(value)) {
    return oneOfMessage('color', Object.values(PRODUCT_MODEL_COLOR))
  }
  return true
}

function dataWeight (value) {
  value = parseFloat(value)
  if (!isFinite(value) || value <= 0 || value >= 1) {
    return numberBetweenMessage('weight', 0, 1)
  }
  return true
}

/**
 * Gets the first ProductQuantity's that is in stock.
 *
 * @example
 *
 * ```js
 * let productQuantityPojos = [
 *   { name : 's', value : 0 },
 *   { name : 'l', value : 42 },
 * ]
 *
 * getFirstProductQuantityInStockProductUtil() => { name : 'l', value : 42 }
 *
 * ````
 *
 * @param  {Object[]} productQuantities
 * @returns {number}
 */
function getFirstProductQuantityInStockProductUtil (productQuantities) {
  productQuantities = sortByArrayUtil(
    productQuantities,
    p => Object.values(PRODUCT_MODEL_SIZE).indexOf(p.data.name)
  )
  return productQuantities.find(
    productQuantity => productQuantity.data.value > 0
  )
}

function getContentSizeGuideProductUtil (content) {
  const re = new RegExp(PRODUCT_MODEL_REGEX.SIZE_GUIDE)
  if (!re.test(content)) {
    return null
  }
  return content.slice(content.indexOf('| '), content.lastIndexOf('|') + 1)
}

function getContentWithoutSizeGuideProductUtil (content) {
  const re = new RegExp(PRODUCT_MODEL_REGEX.SIZE_GUIDE)
  if (!re.test(content)) {
    return content
  }
  return content.slice(content.lastIndexOf('|') + 1)
}

const KEY_VALUE_SEPARATOR = ':'

/**
 * Gets the chunk array of a given chunk.
 * @private
 * @param   {string} chunk
 * @returns {String[]}
 */
function getKeyValueArr (chunk) {
  const arr = chunk.split(KEY_VALUE_SEPARATOR)
  if (arr.length > 2) {
    return [
      arr[0],
      arr.slice(1).join(':')
    ]
  }
  return arr
}

export {
  getValidatorProductUtil,
  getFirstProductQuantityInStockProductUtil,
  getContentSizeGuideProductUtil,
  getContentWithoutSizeGuideProductUtil
}
